import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, MailIcon, PencilIcon, PhoneIcon, PlusIcon, PowerIcon, SearchIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EncabezadoPanel, EstadoVacio, Panel } from '../components/ui/Basicos';
import { Avatar } from '../components/ui/Etiquetas';
import { PuntoSemaforo } from '../components/ui/Semaforo';
import { EditorAbogado } from '../components/abogados/EditorAbogado';
import { useDatos } from '../contexts/DatosContext';
import { inicialesDe } from '../data/abogados';
import { cargaDe, LIMITE_EXPEDIENTES } from '../utils/personal';
import { fechaRel } from '../utils/fechas';
import type { Abogado, Rol } from '../types';
const ROLES: Rol[] = ['Administrador', 'Abogado', 'Recepción/Archivo'];
function abogadoVacio(): Abogado {
  return {
    id: `ab-${Date.now()}`,
    nombre: '',
    cedula: '',
    sucursalId: '',
    especialidades: [],
    correo: '',
    telefono: '',
    rol: 'Abogado',
    fechaIngreso: fechaRel(0),
    activo: true
  };
}
export function Abogados() {
  const {
    abogados,
    expedientes,
    plazos,
    audiencias,
    sucursales,
    sucursalActiva,
    rol,
    agregarAbogado,
    actualizarAbogado,
    alternarEstadoAbogado
  } = useDatos();
  const esAdmin = rol === 'Administrador';
  const [busqueda, setBusqueda] = useState('');
  const [sucursalFiltro, setSucursalFiltro] = useState('todas');
  const [rolFiltro, setRolFiltro] = useState<'todos' | Rol>('todos');
  const [estadoFiltro, setEstadoFiltro] = useState<'todos' | 'activos' | 'inactivos'>('todos');
  const [editando, setEditando] = useState<{
    abogado: Abogado;
    esNuevo: boolean;
  } | null>(null);
  const [aviso, setAviso] = useState('');
  const filas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return abogados.filter(a => {
      if (sucursalActiva !== 'todas' && a.sucursalId !== sucursalActiva) return false;
      if (sucursalFiltro !== 'todas' && a.sucursalId !== sucursalFiltro) return false;
      if (rolFiltro !== 'todos' && a.rol !== rolFiltro) return false;
      if (estadoFiltro === 'activos' && !a.activo) return false;
      if (estadoFiltro === 'inactivos' && a.activo) return false;
      if (q && ![a.nombre, a.cedula, a.correo, a.telefono, a.especialidades.join(' ')].join(' ').toLowerCase().includes(q)) return false;
      return true;
    }).map(a => cargaDe(a, expedientes, plazos, audiencias));
  }, [abogados, expedientes, plazos, audiencias, busqueda, sucursalFiltro, rolFiltro, estadoFiltro, sucursalActiva]);
  const guardar = (abogado: Abogado) => {
    if (editando?.esNuevo) {
      agregarAbogado(abogado);
      setAviso(`${abogado.nombre} se agregó al despacho y ya puede recibir expedientes.`);
    } else {
      actualizarAbogado(abogado);
      setAviso(`${abogado.nombre} actualizado.`);
    }
    setEditando(null);
  };
  const claseSelect = 'h-9 rounded border border-gris-200 bg-white px-2.5 text-sm text-gris-700';
  const sobrecargados = filas.filter(f => f.sobrecargado).length;
  return <>
      <EncabezadoPagina titulo="Abogados y personal" descripcion="Alta del personal del despacho, su rol en el sistema y la carga de trabajo que tiene cada uno." acciones={esAdmin ? <button type="button" onClick={() => setEditando({
      abogado: abogadoVacio(),
      esNuevo: true
    })} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1009" data-mp="3515m3qi12woe">
              <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1010" data-mp="2hcmc96jbybu7" />
              Agregar abogado
            </button> : undefined} data-id="element-1008" data-mp="3sox2p6alpvvh" />

      {aviso && <p role="status" className="mb-4 rounded border border-exito-100 bg-exito-50 px-4 py-2 text-xs font-medium text-exito-700" data-id="element-1011" data-mp="rufrflcm16g4">
          {aviso}
        </p>}

      <Panel data-id="element-1012" data-mp="11k8rpq7axz52">
        {editando && esAdmin && <EditorAbogado abogado={editando.abogado} esNuevo={editando.esNuevo} onGuardar={guardar} onCancelar={() => setEditando(null)} data-id="element-1013" data-mp="11m3bv2ar1n83" />}

        <EncabezadoPanel titulo="Personal del despacho" descripcion={`${filas.length} personas · ${abogados.filter(a => a.activo).length} activas${sobrecargados > 0 ? ` · ${sobrecargados} con señal de sobrecarga` : ''}`} data-id="element-1014" data-mp="1bgvb0ajzci4z" />

        <div className="flex flex-wrap items-center gap-2 border-b border-gris-200 px-5 py-3" data-id="element-1015" data-mp="1fll7a0ijvyqf">
          <label className="relative flex min-w-[16rem] flex-1 items-center" data-id="element-1016" data-mp="3mbspxd3mp4zq">
            <span className="sr-only" data-id="element-1017" data-mp="3hc6bx1fz7kpv">Buscar personal</span>
            <SearchIcon className="pointer-events-none absolute left-3 h-4 w-4 text-gris-400" aria-hidden="true" data-id="element-1018" data-mp="3hdr5oouguzmo" />
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre, cédula, correo o materia…" className="h-9 w-full rounded border border-gris-200 pl-9 pr-3 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500" data-id="element-1019" data-mp="21rnnsbqf9wwl" />
          </label>
          <select aria-label="Filtrar por sucursal" value={sucursalFiltro} onChange={e => setSucursalFiltro(e.target.value)} className={claseSelect} data-id="element-1020" data-mp="1j27h16o5987q">
            <option value="todas" data-id="element-1021" data-mp="193hzxg42nrsj">Todas las sucursales</option>
            {sucursales.map(s => <option key={s.id} value={s.id} data-id="element-1022" data-mp="2zfxs4xdokwqv">
                {s.nombre}
              </option>)}
          </select>
          <select aria-label="Filtrar por rol" value={rolFiltro} onChange={e => setRolFiltro(e.target.value as 'todos' | Rol)} className={claseSelect} data-id="element-1023" data-mp="33grg1qr4ibjr">
            <option value="todos" data-id="element-1024" data-mp="b97ace1kl28p">Todos los roles</option>
            {ROLES.map(r => <option key={r} value={r} data-id="element-1025" data-mp="79gnglf5z69g">
                {r}
              </option>)}
          </select>
          <select aria-label="Filtrar por estado" value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value as 'todos' | 'activos' | 'inactivos')} className={claseSelect} data-id="element-1026" data-mp="2bwz2sor5d3s">
            <option value="todos" data-id="element-1027" data-mp="q79eapcijpqh">Todos los estados</option>
            <option value="activos" data-id="element-1028" data-mp="q5oiyxidk2rk">Activos</option>
            <option value="inactivos" data-id="element-1029" data-mp="l9hlzu7yi1lr">Inactivos</option>
          </select>
        </div>

        {filas.length === 0 ? <EstadoVacio mensaje="Nadie coincide con los filtros" sugerencia="Ajusta la búsqueda, la sucursal, el rol o el estado." data-id="element-1030" data-mp="1ipn4c2rly7f0" /> : <div className="overflow-x-auto scroll-fino" data-id="element-1031" data-mp="1l9jtlj0mnd56">
            <table className="w-full min-w-[1180px] text-sm" data-id="element-1032" data-mp="1q7lb3zien4ej">
              <thead data-id="element-1033" data-mp="1q84snf457qs1">
                <tr className="bg-gris-50 text-left text-2xs uppercase tracking-wide text-gris-500" data-id="element-1034" data-mp="1q9pfsjo4o7fv">
                  <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-1035" data-mp="1v92afql8qit0">
                    Persona
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1036" data-mp="247nn554173lv">
                    Cédula
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1037" data-mp="295fg82gewvrn">
                    Rol
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1038" data-mp="2dxpeq6vlf45j">
                    Sucursal
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1039" data-mp="2iyd0oe4ysusw">
                    Contacto
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1040" data-mp="2rwp2lcba7f0n">
                    Carga de trabajo
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1041" data-mp="3d6vrs37tohsq">
                    Vencimientos
                  </th>
                  <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-1042" data-mp="388khxshj27ai">
                    Estado
                  </th>
                  {esAdmin && <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-1043" data-mp="3i6qhw4jrx3cq">
                      Acciones
                    </th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gris-100" data-id="element-1044" data-mp="2pj44y5onomd7">
                {filas.map(({
              abogado: a,
              activos,
              expedientes: asignados,
              plazos: susPlazos,
              criticos,
              sobrecargado
            }) => {
              const sucursal = sucursales.find(s => s.id === a.sucursalId);
              return <tr key={a.id} className={`align-top ${a.activo ? '' : 'bg-gris-50/60'}`} data-id="element-1045" data-mp="2bq4mpvxhoe1y">
                      <td className="px-5 py-3" data-id="element-1046" data-mp="26smop0l0fj8q">
                        <div className="flex items-center gap-3" data-id="element-1047" data-mp="26pqof5lowih4">
                          <Avatar iniciales={inicialesDe(a.nombre)} foto={a.foto} tamano="md" nombre={a.nombre} tono={a.activo ? 'marino' : 'gris'} data-id="element-1048" data-mp="m90qud4j7115" />
                          <div className="min-w-0" data-id="element-1049" data-mp="hb921rz6nv16">
                            <Link to={`/abogados/${a.id}`} className={`block truncate text-sm font-semibold hover:underline ${a.activo ? 'text-marino-800' : 'text-gris-400'}`} data-id="element-1050" data-mp="14zs8s32l3zr5">
                              {a.nombre}
                            </Link>
                            <p className="mt-0.5 text-2xs text-gris-400" data-id="element-1051" data-mp="3gpl38qnxl8ul">
                              {a.especialidades.length > 0 ? a.especialidades.join(' · ') : 'Sin materias asignadas'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="tabular px-3 py-3 text-xs text-gris-700" data-id="element-1052" data-mp="13jetrwh5r1v7">{a.cedula}</td>
                      <td className="px-3 py-3 text-xs text-gris-700" data-id="element-1053" data-mp="13hlvb4gp7nya">{a.rol}</td>
                      <td className="px-3 py-3 text-xs text-gris-700" data-id="element-1054" data-mp="17ixg8womnl05">{sucursal?.nombre ?? '—'}</td>
                      <td className="px-3 py-3" data-id="element-1055" data-mp="tnbtyx9s3fbf">
                        <p className="inline-flex items-center gap-1.5 text-2xs text-gris-600" data-id="element-1056" data-mp="yklqsxzhyt9a">
                          <MailIcon className="h-3.5 w-3.5 text-gris-400" aria-hidden="true" data-id="element-1057" data-mp="klccy4yrkb8h" />
                          {a.correo}
                        </p>
                        <p className="tabular mt-0.5 inline-flex items-center gap-1.5 text-2xs text-gris-600" data-id="element-1058" data-mp="onz7weqqec9w">
                          <PhoneIcon className="h-3.5 w-3.5 text-gris-400" aria-hidden="true" data-id="element-1059" data-mp="2j06vt84mxsw6" />
                          {a.telefono || '—'}
                        </p>
                      </td>
                      <td className="px-3 py-3" data-id="element-1060" data-mp="3b8fr2xd54vv">
                        <p className="tabular text-sm font-semibold text-marino-900" data-id="element-1061" data-mp="3cjhyyb5u4kv">
                          {activos}
                          <span className="ml-1 text-2xs font-normal text-gris-400" data-id="element-1062" data-mp="h3y2xaomofuk">
                            activos de {asignados.length}
                          </span>
                        </p>
                        <div className="mt-1.5 h-1.5 w-28 overflow-hidden rounded-full bg-gris-200" data-id="element-1063" data-mp="r2xdiysyelk7">
                          <div className={`h-full rounded-full ${sobrecargado ? 'bg-peligro-500' : 'bg-marino-700'}`} style={{
                      width: `${Math.min(100, activos / LIMITE_EXPEDIENTES * 100)}%`
                    }} data-id="element-1064" data-mp="m3u83d92adbp" />
                        </div>
                        {sobrecargado && <p className="mt-1 inline-flex items-center gap-1 text-2xs font-semibold text-peligro-600" data-id="element-1065" data-mp="10rxyavx4i7zv">
                            <AlertTriangleIcon className="h-3 w-3" aria-hidden="true" data-id="element-1066" data-mp="15pzi5j2dibce" />
                            Sobrecargado
                          </p>}
                      </td>
                      <td className="px-3 py-3" data-id="element-1067" data-mp="1eob6tfjoiz27">
                        <p className="tabular text-xs text-gris-700" data-id="element-1068" data-mp="1jqa9sobibtym">{susPlazos.length} vigentes</p>
                        {criticos > 0 && <p className="mt-1 inline-flex items-center gap-1.5 text-2xs font-semibold text-peligro-700" data-id="element-1069" data-mp="1omil1qml330i">
                            <PuntoSemaforo nivel="rojo" data-id="element-1070" data-mp="1yeeu1wq7kdji" />
                            {criticos} crítico{criticos > 1 ? 's' : ''}
                          </p>}
                      </td>
                      <td className="px-3 py-3" data-id="element-1071" data-mp="27etgx0itm0p4">
                        <span className={`inline-flex items-center rounded px-2 py-0.5 text-2xs font-medium ring-1 ${a.activo ? 'bg-exito-50 text-exito-700 ring-exito-100' : 'bg-gris-100 text-gris-500 ring-gris-200'}`} data-id="element-1072" data-mp="27fmepbzsxfv4">
                          {a.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      {esAdmin && <td className="px-5 py-3" data-id="element-1073" data-mp="agvx3blaau4o">
                          <div className="flex items-center justify-end gap-1" data-id="element-1074" data-mp="ah5nh0a8r3aw">
                            <button type="button" onClick={() => setEditando({
                      abogado: a,
                      esNuevo: false
                    })} aria-label={`Editar ${a.nombre}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-1075" data-mp="5ovt9823fh2t">
                              <PencilIcon className="h-4 w-4" data-id="element-1076" data-mp="hytr2zu9qggg" />
                            </button>
                            <button type="button" onClick={() => {
                      alternarEstadoAbogado(a.id);
                      setAviso(`${a.nombre} quedó ${a.activo ? 'inactivo: conserva su historial pero no recibirá nuevos expedientes' : 'activo'}.`);
                    }} aria-label={`${a.activo ? 'Desactivar' : 'Activar'} ${a.nombre}`} className={`flex h-8 w-8 items-center justify-center rounded border border-transparent transition-colors duration-150 hover:border-gris-200 ${a.activo ? 'text-gris-400 hover:text-ambar-600' : 'text-exito-600 hover:text-exito-700'}`} data-id="element-1077" data-mp="3zkgqgsqugke">
                              <PowerIcon className="h-4 w-4" data-id="element-1078" data-mp="1yit0ayv6900c" />
                            </button>
                          </div>
                        </td>}
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </Panel>
    </>;
}